import { httpClient } from './http-client';
import { API_CONFIG, STORAGE_KEYS } from './api.config';
import type { ApiResponse } from './api.types';
import type { ArchivoDto } from '@/modelos/Archivo';

function buildApiUrl(endpoint: string): string {
  const empresaId = localStorage.getItem(STORAGE_KEYS.EMPRESA_ID) ?? '';
  const url = new URL(
    `${API_CONFIG.BASE_URL}/api/${API_CONFIG.API_VERSION}${endpoint}`,
    window.location.origin,
  );
  if (empresaId) url.searchParams.set('empresaId', empresaId);
  return url.toString();
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const empresaId = localStorage.getItem(STORAGE_KEYS.EMPRESA_ID);
  if (empresaId) headers['X-Empresa-Id'] = empresaId;
  const establecimientoId = localStorage.getItem(STORAGE_KEYS.ESTABLECIMIENTO_ID);
  if (establecimientoId) headers['X-Establecimiento-Id'] = establecimientoId;
  return headers;
}

async function parseApiResponse<T>(response: Response): Promise<ApiResponse<T>> {
  const json = await response.json();
  if (!response.ok) {
    const msg = json?.mensaje || json?.message || json?.error || `Error HTTP ${response.status}`;
    throw new Error(msg);
  }
  const data = ('exito' in json && 'data' in json) ? json.data : json;
  return { data: data as T, status: response.status, ok: true };
}

export const archivosService = {
  subirParaProducto: async (
    productoId: string,
    file: File,
    opts: { esPrincipal?: boolean; etiqueta?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.esPrincipal !== undefined) formData.append('esPrincipal', String(opts.esPrincipal));
    if (opts.etiqueta) formData.append('etiqueta', opts.etiqueta);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/producto/${productoId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeProducto: (productoId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/producto/${productoId}`),

  subirParaCliente: async (
    clienteId: string,
    file: File,
    opts: { esPrincipal?: boolean; etiqueta?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.esPrincipal !== undefined) formData.append('esPrincipal', String(opts.esPrincipal));
    if (opts.etiqueta) formData.append('etiqueta', opts.etiqueta);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/cliente/${clienteId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeCliente: (clienteId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/cliente/${clienteId}`),

  subirParaUsuario: async (
    usuarioId: string,
    file: File,
    opts: { esPrincipal?: boolean; etiqueta?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.esPrincipal !== undefined) formData.append('esPrincipal', String(opts.esPrincipal));
    if (opts.etiqueta) formData.append('etiqueta', opts.etiqueta);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/usuario/${usuarioId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeUsuario: (usuarioId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/usuario/${usuarioId}`),

  eliminarDeUsuario: (usuarioId: string, id: string): Promise<ApiResponse<void>> =>
    httpClient.delete<void>(`/archivos/usuario/${usuarioId}/${id}`),

  obtenerUrlTemporalDeUsuario: (usuarioId: string, id: string): Promise<ApiResponse<{ url: string }>> =>
    httpClient.get<{ url: string }>(`/archivos/usuario/${usuarioId}/${id}/url-temporal`),

  subirParaDiseno: async (
    disenoId: string,
    file: File,
    opts: { esPrincipal?: boolean; etiqueta?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.esPrincipal !== undefined) formData.append('esPrincipal', String(opts.esPrincipal));
    if (opts.etiqueta) formData.append('etiqueta', opts.etiqueta);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/diseno-comprobante/${disenoId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeDiseno: (disenoId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/diseno-comprobante/${disenoId}`),

  subirParaEmpresa: async (
    empresaId: string,
    file: File,
    opts: { esPrincipal?: boolean; etiqueta?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.esPrincipal !== undefined) formData.append('esPrincipal', String(opts.esPrincipal));
    if (opts.etiqueta) formData.append('etiqueta', opts.etiqueta);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/empresa/${empresaId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeEmpresa: (empresaId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/empresa/${empresaId}`),

  subirEvidenciaHabilitacion: async (
    empresaId: string,
    file: File,
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    const response = await fetch(buildApiUrl(`/archivos/habilitacion-pse/${empresaId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerEvidenciaHabilitacion: (empresaId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/habilitacion-pse/${empresaId}`),

  subirParaCobranza: async (
    cobranzaId: string,
    file: File,
    opts: { etiqueta?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.etiqueta) formData.append('etiqueta', opts.etiqueta);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/cobranza/${cobranzaId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeCobranza: (cobranzaId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/cobranza/${cobranzaId}`),

  subirParaRequerimiento: async (
    requerimientoId: string,
    file: File,
    opts: { tipoAdjunto?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.tipoAdjunto) formData.append('tipoAdjunto', opts.tipoAdjunto);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/requerimiento/${requerimientoId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeRequerimiento: (requerimientoId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/requerimiento/${requerimientoId}`),

  subirParaGasto: async (
    gastoId: string,
    file: File,
    opts: { tipoAdjunto?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.tipoAdjunto) formData.append('tipoAdjunto', opts.tipoAdjunto);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/gasto/${gastoId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeGasto: (gastoId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/gasto/${gastoId}`),

  subirParaOrdenCompra: async (
    ordenCompraId: string,
    file: File,
    opts: { tipoAdjunto?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.tipoAdjunto) formData.append('tipoAdjunto', opts.tipoAdjunto);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/orden-compra/${ordenCompraId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeOrdenCompra: (ordenCompraId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/orden-compra/${ordenCompraId}`),

  subirParaComprobanteCompra: async (
    comprobanteCompraId: string,
    file: File,
    opts: { tipoAdjunto?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.tipoAdjunto) formData.append('tipoAdjunto', opts.tipoAdjunto);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/comprobante-compra/${comprobanteCompraId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDeComprobanteCompra: (comprobanteCompraId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/comprobante-compra/${comprobanteCompraId}`),

  subirParaPago: async (
    pagoId: string,
    file: File,
    opts: { etiqueta?: string; orden?: number } = {},
  ): Promise<ApiResponse<ArchivoDto>> => {
    const formData = new FormData();
    formData.append('archivo', file, file.name);
    if (opts.etiqueta) formData.append('etiqueta', opts.etiqueta);
    if (opts.orden !== undefined) formData.append('orden', String(opts.orden));
    const response = await fetch(buildApiUrl(`/archivos/pago/${pagoId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return parseApiResponse<ArchivoDto>(response);
  },

  obtenerDePago: (pagoId: string): Promise<ApiResponse<ArchivoDto[]>> =>
    httpClient.get<ArchivoDto[]>(`/archivos/pago/${pagoId}`),

  eliminar: (id: string): Promise<ApiResponse<void>> =>
    httpClient.delete<void>(`/archivos/${id}`),

  marcarPrincipal: (id: string): Promise<ApiResponse<void>> =>
    httpClient.patch<void>(`/archivos/${id}/principal`),

  obtenerUrlTemporal: (id: string): Promise<ApiResponse<{ url: string }>> =>
    httpClient.get<{ url: string }>(`/archivos/${id}/url-temporal`),
};

export async function resolverFotoUsuario(
  usuarioId: string,
): Promise<{ url: string | null; archivoId: string | null }> {
  try {
    const res = await archivosService.obtenerDeUsuario(usuarioId);
    const avatar = (res.data ?? []).find(a => a.esPrincipal) ?? (res.data ?? [])[0];
    if (!avatar) return { url: null, archivoId: null };
    if (avatar.urlPublica) return { url: avatar.urlPublica, archivoId: avatar.id };
    const temporal = await archivosService.obtenerUrlTemporalDeUsuario(usuarioId, avatar.id);
    return { url: temporal.data?.url ?? null, archivoId: avatar.id };
  } catch {
    return { url: null, archivoId: null };
  }
}

export async function resolveArchivosUrls(archivos: ArchivoDto[]): Promise<ArchivoDto[]> {
  return Promise.all(
    archivos.map(async (a) => {
      if (a.urlPublica) return a;
      try {
        const res = await archivosService.obtenerUrlTemporal(a.id);
        return { ...a, urlPublica: res.data?.url ?? null };
      } catch {
        return a;
      }
    }),
  );
}
